import React, { useState, useEffect } from 'react';
import { 
    Box, Button, Typography, Card, Table, TableBody, TableCell, 
    TableContainer, TableHead, TableRow, IconButton, Tooltip, 
    Drawer, Grid, TextField, Switch, FormControlLabel, Divider,
    LinearProgress, Stack, Chip, useTheme
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faPlus, faPenToSquare, faXmark, faTicket, faEyeSlash
} from '@fortawesome/free-solid-svg-icons';
import { useParams } from 'react-router-dom';


import { getEventSections, createSection, updateSection, toggleSectionStatus } from '../../../../controllers/company/events/MerchantEventSectionsController';

export default function EventSections() {
    const theme = useTheme();
    const { id } = useParams(); // ID o UUID del evento desde la URL
    
    const [sections, setSections] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    
    // Estados del Drawer (Panel Lateral)
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [editingUuid, setEditingUuid] = useState(null);

    // Estado del Formulario
    const initialFormState = {
        name: '',
        description: '',
        price: '',
        capacity: '',
        multiples: 1,
        max_selection: 4,
        status_code: true,       // 1 o 0
        is_visible: true,        // 1 o 0
        show_availability: true  // 1 o 0
    };
    const [formData, setFormData] = useState(initialFormState);

    useEffect(() => {
        fetchSections();
    }, [id]);

    const fetchSections = async () => {
        setIsLoading(true);
        let response = await getEventSections(id);
        if(response.success)
            setSections(response.data.sections);
        
        setIsLoading(false);
    };

    // --- MANEJO DEL DRAWER Y FORMULARIO ---
    const handleOpenDrawer = (section = null) => {
        if (section) {
            setEditingUuid(section.uuid);
            setFormData({
                name: section.name,
                description: section.description || '',
                price: section.price,
                capacity: section.capacity,
                multiples: section.multiples,
                max_selection: section.max_selection || 4,
                status_code: Boolean(section.status_code),
                is_visible: Boolean(section.is_visible),
                show_availability: Boolean(section.show_availability)
            });
        } else {
            setEditingUuid(null);
            setFormData(initialFormState);
        }
        setIsDrawerOpen(true);
    };

    const handleCloseDrawer = () => {
        setIsDrawerOpen(false);
        setFormData(initialFormState);
        setEditingUuid(null);
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData({
            ...formData,
            [name]: type === 'checkbox' ? checked : value
        });
    };

    const handleSubmit = async () => {
        if (!formData.name || !formData.price || !formData.capacity) return;
        
        setIsSubmitting(true);
        
        // Preparamos la data para el backend (Convirtiendo booleanos a 1/0)
        const payload = {
            event_id: id,
            ...formData,
            //status_code: formData.status_code ? 1 : 0,
            is_visible: formData.is_visible ? 1 : 0,
            show_availability: formData.show_availability ? 1 : 0,
        };

        if (editingUuid) {
            await updateSection(editingUuid, payload);
        } else {
            await createSection(payload);
        }
        
        setIsSubmitting(false);
        fetchSections();
        handleCloseDrawer();
    };

    const handleToggleStatus = async (uuid, currentStatus) => {
        // Actualización optimista en la UI
        setSections(sections.map(s => s.uuid === uuid ? { ...s, is_visible: !currentStatus } : s));
        await toggleSectionStatus(uuid);
    };

    return (
        <Box>
            {/* --- CABECERA Y BOTÓN DE ACCIÓN --- */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Box>
                    <Typography variant="h5" fontWeight="bold" color="#111">
                        Zonas y Secciones
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Configura los precios, aforos y visibilidad de los boletos.
                    </Typography>
                </Box>
                <Button 
                    variant="contained" 
                    color="primary" 
                    startIcon={<FontAwesomeIcon icon={faPlus} />}
                    onClick={() => handleOpenDrawer()}
                    disableElevation
                    sx={{ borderRadius: 2, fontWeight: 'bold', textTransform: 'none', px: 3 }}
                >
                    Crear Sección
                </Button>
            </Box>

            {/* --- TABLA PRINCIPAL A PANTALLA COMPLETA --- */}
            <TableContainer component={Card} elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 3 }}>
                <Table sx={{ minWidth: 650 }}>
                    <TableHead sx={{ bgcolor: '#f8f9fa' }}>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold', color: '#555' }}>Nombre de la Sección</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', color: '#555' }}>Capacidad y Progreso</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', color: '#555' }}>Precio</TableCell>
                            <TableCell align="center" sx={{ fontWeight: 'bold', color: '#555' }}>Estatus</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold', color: '#555' }}>Acciones</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {isLoading ? (
                            <TableRow><TableCell colSpan={5} align="center" sx={{ py: 5 }}>Cargando secciones...</TableCell></TableRow>
                        ) : sections.length === 0 ? (
                            <TableRow><TableCell colSpan={5} align="center" sx={{ py: 5 }}>No hay secciones configuradas aún.</TableCell></TableRow>
                        ) : (
                            sections.map((row) => (
                                <TableRow key={row.uuid} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                    
                                    {/* Nombre y Descripción */}
                                    <TableCell>
                                        <Box display="flex" alignItems="center" gap={1.5}>
                                            <Typography variant="subtitle2" fontWeight="bold" color="#222">
                                                {row.name}
                                            </Typography>
                                            {!row.is_visible && (
                                                <Tooltip title="Oculto al público">
                                                    <Chip icon={<FontAwesomeIcon icon={faEyeSlash} />} label="Oculto" size="small" sx={{ height: 20, fontSize: '0.65rem' }} />
                                                </Tooltip>
                                            )}
                                        </Box>
                                        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 250 }} noWrap>
                                            {row.description || 'Sin descripción'}
                                        </Typography>
                                    </TableCell>
                                    
                                    {/* Capacidad y Barra de Progreso */}
                                    <TableCell sx={{ minWidth: 200 }}>
                                        <Box display="flex" justifyContent="space-between" mb={0.5}>
                                            <Typography variant="caption" fontWeight="bold" color="text.secondary">
                                                {row.sold} / {row.capacity} vendidos
                                            </Typography>
                                        </Box>
                                        <LinearProgress 
                                            variant="determinate" 
                                            value={row.capacity > 0 ? (row.sold / row.capacity) * 100 : 0} 
                                            color={row.sold >= row.capacity ? "success" : "primary"}
                                            sx={{ height: 6, borderRadius: 3, bgcolor: '#f0f0f0' }}
                                        />
                                    </TableCell>
                                    
                                    {/* Precio */}
                                    <TableCell>
                                        <Typography variant="subtitle2" fontWeight="bold">
                                            {row.price > 0 ? `$${parseFloat(row.price).toFixed(2)}` : 'GRATIS'}
                                        </Typography>
                                    </TableCell>
                                    
                                    {/* Toggle de Estatus Rápido */}
                                    <TableCell align="center">
                                        <Tooltip title={row.is_visible ? "Desactivar Ventas" : "Activar Ventas"}>
                                            <Switch 
                                                checked={Boolean(row.is_visible)} 
                                                onChange={() => handleToggleStatus(row.uuid, row.is_visible)}
                                                color="success"
                                                size="small"
                                            />
                                        </Tooltip>
                                    </TableCell>
                                    
                                    {/* Acciones */}
                                    <TableCell align="right">
                                        <Tooltip title="Editar sección">
                                            <IconButton onClick={() => handleOpenDrawer(row)} size="small" sx={{ color: '#1976d2', bgcolor: '#e3f2fd', '&:hover': { bgcolor: '#bbdefb' } }}>
                                                <FontAwesomeIcon icon={faPenToSquare} size="sm" />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* --- PANEL LATERAL (DRAWER) --- */}
            <Drawer 
                anchor="right" 
                open={isDrawerOpen} 
                onClose={handleCloseDrawer}
                PaperProps={{ sx: { width: { xs: '100%', sm: 450 }, bgcolor: '#fafafa' } }}
            >
                <Box display="flex" flexDirection="column" height="100%">
                    
                    {/* Drawer Header */}
                    <Box px={3} py={2.5} display="flex" justifyContent="space-between" alignItems="center" bgcolor="#fff" borderBottom="1px solid #eee">
                        <Typography variant="h6" fontWeight="bold">
                            {editingUuid ? 'Editar Sección' : 'Nueva Sección'}
                        </Typography>
                        <IconButton onClick={handleCloseDrawer} size="small"><FontAwesomeIcon icon={faXmark} /></IconButton>
                    </Box>

                    {/* Drawer Content (Formulario Scrollable) */}
                    <Box p={3} flexGrow={1} overflow="auto">
                        
                        {/* Bloque 1: Información Básica */}
                        <Typography variant="subtitle2" color="primary.main" fontWeight="bold" textTransform="uppercase" mb={2}>1. Información Básica</Typography>
                        <Grid container spacing={2} mb={4}>
                            <Grid item xs={12}>
                                <TextField 
                                    label="Nombre de la sección *" name="name" fullWidth size="small"
                                    value={formData.name} onChange={handleChange}
                                    sx={{ bgcolor: '#fff', '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField 
                                    label="Descripción" name="description" fullWidth size="small" multiline rows={2}
                                    placeholder="Ej: Incluye acceso rápido y 2 tragos."
                                    value={formData.description} onChange={handleChange}
                                    sx={{ bgcolor: '#fff', '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                />
                            </Grid>
                        </Grid>

                        {/* Bloque 2: Precios y Límites */}
                        <Typography variant="subtitle2" color="primary.main" fontWeight="bold" textTransform="uppercase" mb={2}>2. Precios y Límites</Typography>
                        <Grid container spacing={2} mb={4}>
                            <Grid item xs={6}>
                                <TextField 
                                    label="Precio (USD) *" name="price" type="number" fullWidth size="small"
                                    value={formData.price} onChange={handleChange}
                                    sx={{ bgcolor: '#fff', '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField 
                                    label="Aforo (Capacidad) *" name="capacity" type="number" fullWidth size="small"
                                    value={formData.capacity} onChange={handleChange}
                                    sx={{ bgcolor: '#fff', '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField 
                                    label="Vender en Múltiplos de *" name="multiples" type="number" fullWidth size="small"
                                    value={formData.multiples} onChange={handleChange}
                                    helperText="Normalmente es 1"
                                    sx={{ bgcolor: '#fff', '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField 
                                    label="Max. Boletos por compra" name="max_selection" type="number" fullWidth size="small"
                                    value={formData.max_selection} onChange={handleChange}
                                    helperText="Evita revendedores"
                                    sx={{ bgcolor: '#fff', '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                />
                            </Grid>
                        </Grid>

                        {/* Bloque 3: Visibilidad y Controles */}
                        <Typography variant="subtitle2" color="primary.main" fontWeight="bold" textTransform="uppercase" mb={2}>3. Controles de Publicación</Typography>
                        <Card elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2 }}>
                            <Stack divider={<Divider />}>
                                {/*<Box px={2} py={1.5} display="flex" justifyContent="space-between" alignItems="center">
                                    <Box>
                                        <Typography variant="body2" fontWeight="bold">Sección Activa</Typography>
                                        <Typography variant="caption" color="text.secondary">Permite vender boletos en esta zona.</Typography>
                                    </Box>
                                    <Switch name="status_code" checked={formData.status_code} onChange={handleChange} color="success" />
                                </Box>*/}
                                <Box px={2} py={1.5} display="flex" justifyContent="space-between" alignItems="center">
                                    <Box>
                                        <Typography variant="body2" fontWeight="bold">Visible al Público</Typography>
                                        <Typography variant="caption" color="text.secondary">Muestra esta zona en la página web.</Typography>
                                    </Box>
                                    <Switch name="is_visible" checked={formData.is_visible} onChange={handleChange} color="link" />
                                </Box>
                                <Box px={2} py={1.5} display="flex" justifyContent="space-between" alignItems="center">
                                    <Box>
                                        <Typography variant="body2" fontWeight="bold">Mostrar Disponibilidad</Typography>
                                        <Typography variant="caption" color="text.secondary">Muestra al cliente cuántos tickets quedan.</Typography>
                                    </Box>
                                    <Switch name="show_availability" checked={formData.show_availability} onChange={handleChange} color="link" />
                                </Box>
                            </Stack>
                        </Card>
                        <Box pb={4} /> {/* Espaciado inferior */}
                    </Box>

                    {/* Drawer Footer (Botones) */}
                    <Box p={3} bgcolor="#fff" borderTop="1px solid #eee">
                        <LoadingButton 
                            loading={isSubmitting} variant="contained" color="primary" fullWidth size="large"
                            onClick={handleSubmit} disableElevation
                            sx={{ borderRadius: 2, fontWeight: 'bold', textTransform: 'none' }}
                        >
                            Guardar Sección
                        </LoadingButton>
                    </Box>

                </Box>
            </Drawer>
        </Box>
    );
}